import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SEO from '../../components/SEO';
import ShareBox from '../../components/ShareBox';
import { 
  TrendingUp, TrendingDown, Shield, Target, ArrowRight, ArrowLeft,
  ChevronRight, Zap, AlertTriangle, CheckCircle, Clock, XCircle, BookOpen, BarChart3, Brain, Activity
} from 'lucide-react';

// FAQ Data for Schema
const faqData = [
  {
    question: "LONG ne demek?",
    answer: "LONG, fiyat artışı beklentisine dayalı işlem yönüdür."
  },
  {
    question: "SHORT ne demek?",
    answer: "SHORT, fiyat düşüşü beklentisine dayalı işlem yönüdür."
  },
  {
    question: "LONG işlemde nasıl kâr edilir?",
    answer: "Fiyat hedef seviyelere ulaştığında senaryo olumlu sonuçlanır."
  },
  {
    question: "SHORT işlem ne zaman kullanılır?",
    answer: "Fiyat düşüş beklentisi oluştuğunda değerlendirilir."
  },
  {
    question: "LONG ve SHORT garanti midir?",
    answer: "Hayır. Olasılık temellidir, kesinlik içermez."
  }
];

const structuredData = [
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "LONG ve SHORT Ne Demek? Kripto İşlemlerde Yön Mantığı",
    "description": "LONG ve SHORT ne anlama gelir? Kripto işlemlerde yükseliş ve düşüş senaryolarını basitçe öğrenin.",
    "author": {
      "@type": "Organization",
      "name": "Borsamex"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Borsamex",
      "logo": {
        "@type": "ImageObject",
        "url": "https://borsamex.com/logo512.png"
      }
    },
    "datePublished": "2025-12-24",
    "dateModified": "2025-12-24",
    "mainEntityOfPage": "https://borsamex.com/rehber/long-ve-short-ne-demek"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const LongShortNedirPage = () => {
  return (
    <div className="min-h-screen bg-brand-dark">
      <SEO 
        title="LONG ve SHORT Ne Demek? Kripto İşlemlerde Yön Mantığı | Borsamex"
        description="LONG ve SHORT ne anlama gelir? Kripto işlemlerde yükseliş ve düşüş senaryolarını basitçe öğrenin. Long/Short mantığını net şekilde keşfedin."
        keywords="long ne demek, short ne demek, long short nedir, kripto long short, short işlem nedir, long işlem nedir"
        canonical="https://borsamex.com/rehber/long-ve-short-ne-demek"
        structuredData={structuredData}
      />

      {/* Breadcrumb */}
      <div className="border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/rehber" className="hover:text-white transition-colors">Rehber</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">LONG ve SHORT Ne Demek?</span>
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          
          {/* Header */}
          <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
            <div className="flex items-center gap-3 mb-6">
              <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">Temel</Badge>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Clock className="h-4 w-4" />
                <span>4 dk okuma</span>
              </div>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              LONG ve SHORT Ne Demek?
            </h1>
            
            {/* Featured Snippet Target */}
            <div className="p-6 bg-slate-900/50 border border-slate-700/50 rounded-xl">
              <p className="text-lg text-slate-300 leading-relaxed">
                <strong className="text-green-400">LONG</strong>, fiyatın yükselmesi beklentisiyle açılan işlem senaryosudur.
                <strong className="text-red-400"> SHORT</strong> ise fiyatın düşmesi beklentisine dayalı pozisyonu ifade eder.
              </p>
              <p className="text-slate-400 mt-4">
                Bu terimler yönü belirtir, kesinlik veya garanti içermez.
              </p>
            </div>
          </motion.header>

          {/* Content */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="prose prose-invert prose-slate max-w-none"
          >
            
            {/* LONG Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-green-400" />
                </div>
                LONG Ne Demek?
              </h2>
              
              <p className="text-slate-300 mb-4">
                LONG, bir varlığın fiyatının artacağı beklentisiyle oluşturulan işlem yönüdür.
              </p>
              
              <p className="text-slate-300 mb-4">Bu senaryoda temel mantık:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                  <span>Düşük fiyattan pozisyon açmak</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-green-400 shrink-0 mt-0.5" />
                  <span>Daha yüksek seviyeleri hedeflemek</span>
                </li>
              </ul>
              
              <Card className="border-green-500/30 bg-green-950/20">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    LONG kavramı sadece spot piyasalarda değil, türev işlemlerde de kullanılır.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* SHORT Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="h-4 w-4 text-red-400" />
                </div>
                SHORT Ne Demek?
              </h2>
              
              <p className="text-slate-300 mb-4">
                SHORT, fiyatın düşmesi beklentisiyle oluşturulan işlem yönüdür.
              </p>
              
              <p className="text-slate-300 mb-4">Temel mantık:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Yüksek seviyeden senaryo oluşturmak</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>Daha düşük fiyat hareketini hedeflemek</span>
                </li>
              </ul>
              
              <Card className="border-red-500/30 bg-red-950/20">
                <CardContent className="p-4">
                  <p className="text-slate-300 text-sm">
                    Bu yapı özellikle vadeli işlemlerde yaygındır.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* Fark Section */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-purple-400" />
                </div>
                LONG ve SHORT Arasındaki Fark
              </h2>
              
              <p className="text-slate-300 mb-4">
                İki terim arasındaki temel fark yön beklentisidir:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <Card className="border-green-500/30 bg-green-950/20">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingUp className="h-6 w-6 text-green-400" />
                      <span className="font-bold text-white text-lg">LONG</span>
                    </div>
                    <p className="text-slate-300">Yukarı yönlü senaryo</p>
                  </CardContent>
                </Card>
                
                <Card className="border-red-500/30 bg-red-950/20">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingDown className="h-6 w-6 text-red-400" />
                      <span className="font-bold text-white text-lg">SHORT</span>
                    </div>
                    <p className="text-slate-300">Aşağı yönlü senaryo</p>
                  </CardContent>
                </Card>
              </div>
              
              <p className="text-slate-400">
                Bu kavramlar piyasa yönünü değil, işlem planının mantığını tanımlar.
              </p>
            </section>

            {/* Inline AI Sinyal Kutusu */}
            <section className="mb-12">
              <Card className="border-brand-green/30 bg-brand-green/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-6 w-6 text-brand-green" />
                    <span className="font-bold text-white">AI Sinyal Örneği</span>
                  </div>
                  <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">SOL-USDT</span>
                      <Badge className="bg-red-500/20 text-red-400">SHORT</Badge>
                    </div>
                    <p className="text-slate-400 text-sm mb-2">
                      Giriş: $178.50 | TP1: $172 | TP2: $165 | Stop: $183.80
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Activity className="h-3 w-3" />
                      <span>LONG ve SHORT her iki yönde sinyal üretilir</span>
                    </div>
                  </div>
                  <p className="text-slate-400 text-sm mb-4">
                    Borsamex'in AI sistemi piyasa yönüne göre hem LONG hem SHORT senaryolar oluşturur.
                  </p>
                  <Link to="/signals">
                    <Button variant="outline" className="border-brand-green text-brand-green hover:bg-brand-green/10 w-full">
                      Tüm Sinyalleri İncele <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </section>

            {/* LONG İşlem Mantığı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-green-400" />
                </div>
                LONG İşlem Mantığı
              </h2>
              
              <p className="text-slate-300 mb-4">Bir LONG senaryosunda:</p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Giriş seviyesi belirlenir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Olası hedef seviyeleri tanımlanır (TP)</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk sınırı eklenir (Stop Loss)</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                Amaç tahmin yapmak değil, planlı hareket etmektir.
              </p>
            </section>

            {/* SHORT İşlem Mantığı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <Target className="h-4 w-4 text-red-400" />
                </div>
                SHORT İşlem Mantığı
              </h2>
              
              <p className="text-slate-300 mb-4">SHORT senaryosunda yapı aynıdır:</p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Giriş seviyesi</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Hedef bölgeleri</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>Risk sınırı</span>
                </li>
              </ul>
              
              <p className="text-slate-400">
                Tek fark fiyat beklentisinin yönüdür.
              </p>
            </section>

            {/* Yaygın Yanılgı */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                </div>
                Yaygın Yanılgı
              </h2>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>LONG = Kesin yükseliş <strong className="text-red-400">değildir</strong></span>
                </div>
                <div className="flex items-start gap-3 text-slate-300">
                  <XCircle className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
                  <span>SHORT = Kesin düşüş <strong className="text-red-400">değildir</strong></span>
                </div>
              </div>
              
              <p className="text-slate-400">
                Kripto piyasası dinamik olduğu için tüm senaryolar olasılık temellidir.
              </p>
            </section>

            {/* Risk Yönetimi */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-purple-400" />
                </div>
                LONG / SHORT ve Risk Yönetimi
              </h2>
              
              <p className="text-slate-300 mb-4">
                Profesyonel sistemlerde yön tek başına yeterli değildir.
              </p>
              
              <p className="text-slate-300 mb-4">Yön + Risk Yapısı birlikte değerlendirilir:</p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>Stop Loss seviyesi</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>Volatilite davranışı</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-purple-400 shrink-0 mt-0.5" />
                  <span>Hedef oranı</span>
                </li>
              </ul>

              <div className="flex gap-4">
                <Link to="/rehber/tp-ve-stop-loss-nedir">
                  <Button variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                    TP ve Stop Loss Rehberi →
                  </Button>
                </Link>
              </div>
            </section>

            {/* Borsamex */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-brand-green/20 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-brand-green" />
                </div>
                Borsamex Sistem Yaklaşımı
              </h2>
              
              <p className="text-slate-300 mb-4">Borsamex sinyallerinde:</p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>LONG / SHORT yönü algoritmik analiz ile belirlenir</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>TP ve Stop seviyeleri birlikte sunulur</span>
                </li>
                <li className="flex items-start gap-3 text-slate-300">
                  <CheckCircle className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <span>AI yalnızca bağlam & açıklama üretir</span>
                </li>
              </ul>

              <div className="flex flex-wrap gap-4">
                <Link to="/long-sinyaller">
                  <Button variant="outline" className="border-green-600 text-green-400 hover:bg-green-950">
                    LONG Sinyaller →
                  </Button>
                </Link>
                <Link to="/short-sinyaller">
                  <Button variant="outline" className="border-red-600 text-red-400 hover:bg-red-950">
                    SHORT Sinyaller →
                  </Button>
                </Link>
              </div>
            </section>

            {/* İlgili Konular - Internal Links */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-blue-400" />
                </div>
                İlgili Konular
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <Link to="/rehber/kripto-sinyali-nedir" className="group">
                  <Card className="border-purple-500/20 bg-slate-900/50 hover:border-purple-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Zap className="h-5 w-5 text-purple-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-purple-400 transition-colors">Kripto Sinyali Nedir?</p>
                          <p className="text-xs text-slate-500">Temel kavramları öğren</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/kripto-sinyali-nasil-okunur" className="group">
                  <Card className="border-amber-500/20 bg-slate-900/50 hover:border-amber-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="h-5 w-5 text-amber-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-amber-400 transition-colors">Sinyal Nasıl Okunur?</p>
                          <p className="text-xs text-slate-500">Pratik sinyal okuma rehberi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/rehber/yeni-baslayanlar-icin-risk-yonetimi" className="group">
                  <Card className="border-red-500/20 bg-slate-900/50 hover:border-red-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Shield className="h-5 w-5 text-red-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-red-400 transition-colors">Risk Yönetimi</p>
                          <p className="text-xs text-slate-500">Sermaye koruma stratejileri</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                
                <Link to="/ai-kripto-sinyalleri" className="group">
                  <Card className="border-cyan-500/20 bg-slate-900/50 hover:border-cyan-500/40 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Brain className="h-5 w-5 text-cyan-400" />
                        <div>
                          <p className="font-medium text-white group-hover:text-cyan-400 transition-colors">AI Kripto Sinyalleri</p>
                          <p className="text-xs text-slate-500">Yapay zeka destekli sistem</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            {/* Önemli Not */}
            <section className="mb-12">
              <Card className="border-amber-500/30 bg-amber-950/20">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="h-8 w-8 text-amber-400 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-amber-400 mb-2">Önemli Not</h3>
                      <p className="text-slate-300">
                        LONG ve SHORT kavramları yatırım tavsiyesi değildir.
                        Tüm işlemler risk içerir.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-6">Sık Sorulan Sorular</h2>
              
              <div className="space-y-4">
                {faqData.map((faq, idx) => (
                  <Card key={idx} className="border-slate-700/50 bg-slate-900/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-white mb-2 flex items-start gap-2">
                        <span className="text-brand-green">?</span>
                        {faq.question}
                      </h3>
                      <p className="text-slate-400 text-sm pl-5">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <ShareBox 
              title="LONG ve SHORT Ne Demek? Kripto Islemlerde Yon Mantigi"
              url="/rehber/long-ve-short-ne-demek"
              hashtags="#kripto #long #short #trading"
            />

          </motion.div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-8 border-t border-slate-800">
            <Link to="/rehber/tp-ve-stop-loss-nedir" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              TP ve Stop Loss Nedir?
            </Link>
            <Link to="/rehber/kripto-sinyali-nasil-okunur" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              Sonraki: Kripto Sinyali Nasıl Okunur?
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-blue-900/20 to-purple-900/20 rounded-2xl border border-slate-700/50 text-center">
            <h3 className="text-xl font-bold text-white mb-3">
              LONG ve SHORT Mantığını Öğrendin
            </h3>
            <p className="text-slate-400 mb-6">
              Şimdi canlı sinyalleri takip et ve her iki yönde de fırsatları gör.
            </p>
            <Link to="/register">
              <Button size="lg" className="bg-brand-green hover:bg-brand-green/90 text-brand-dark font-bold px-8">
                Ücretsiz Başla <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

        </div>
      </article>
    </div>
  );
};

export default LongShortNedirPage;
